import React, { useMemo, useCallback } from 'react';
import Plot from 'react-plotly.js';
import { BarChart3 } from 'lucide-react';
import { ComponentData } from '../../types';
import { Card } from '../ui/Card';
import { useTheme } from '../../contexts/ThemeContext';

interface ChartRendererProps {
  data: ComponentData;
  title?: string;
  accent?: 'blue' | 'yellow' | 'purple' | 'green' | 'indigo' | 'emerald' | 'orange' | 'cyan';
}

export const ChartRenderer: React.FC<ChartRendererProps> = ({ data, title, accent }) => {
  const { theme } = useTheme();
  
  // Check if this is a special chart (HTML format) or regular chart (JSON format)
  const isSpecialChart = data.component_kind === 'special_chart';
  
  // Move all hooks to the top before any conditional returns
  const chartConfig = useMemo(() => {
    if (!data.chart_json) {
      console.log('ChartRenderer: No chart_json provided:', data);
      return null;
    }
    
    // For special charts, chart_json contains HTML - don't try to parse as JSON
    if (isSpecialChart) {
      return data.chart_json;
    }
    
    try {
      const parsed = JSON.parse(data.chart_json);
      console.log('ChartRenderer: Parsed chart config:', parsed);
      return parsed;
    } catch (error) {
      console.error('Failed to parse chart JSON:', error, 'Raw data:', data.chart_json);
      return null;
    }
  }, [data, isSpecialChart]);

  const config = useMemo(() => ({
    displayModeBar: false, // Hide the toolbar completely
    displaylogo: false,
    responsive: true,
    scrollZoom: false, // Disable scroll zoom to prevent the error
    doubleClick: false, // Disable double click zoom
    showTips: false, // Disable hover tips on mode bar
    staticPlot: false, // Keep interactive but controlled
  }), []);

  // Add error boundary for Plotly rendering
  const handleError = useCallback((error: any) => {
    console.warn('Plotly render error (non-critical):', error);
  }, []);

  if (!chartConfig) {
    return (
      <Card accent={accent}>
        <div className="text-center py-8">
          <p className="theme-text-chart-empty">Unable to render chart</p>
        </div>
      </Card>
    );
  }
  
  // Handle special charts (HTML format) differently
  if (isSpecialChart && typeof chartConfig === 'string') {
    return (
      <Card accent={accent}>
        <div className="p-6">
          <h3 className="text-lg font-medium text-white mb-4 flex items-center space-x-2">
            <BarChart3 className="w-5 h-5 text-blue-400" />
            <span>{title}</span>
          </h3>
          <div 
            className="w-full"
            dangerouslySetInnerHTML={{ __html: chartConfig }}
            style={{ minHeight: '400px' }}
          />
          <p className="text-xs text-slate-500 mt-2">
            * Precomputed analysis - focus on trends and patterns
          </p>
        </div>
      </Card>
    );
  }

  // Check if we have actual data to display
  const hasData = chartConfig.data && chartConfig.data.length > 0;
  
  // For empty data cases, check if there's an annotation with a message
  const isEmptyDataMessage = !hasData && 
    chartConfig.layout?.annotations && 
    chartConfig.layout.annotations.length > 0;

  if (!hasData && !isEmptyDataMessage) {
    return (
      <Card accent={accent}>
        <div className="text-center py-8">
          <div className="w-16 h-16 theme-bg-chart-icon rounded-2xl flex items-center justify-center mx-auto mb-4">
            <BarChart3 className="w-8 h-8 theme-text-chart-icon" />
          </div>
          <h3 className="text-lg font-medium theme-text-chart-title mb-2">
            {title || 'Chart'}
          </h3>
          <p className="theme-text-chart-empty">
            No data available for this visualization
          </p>
        </div>
      </Card>
    );
  }

  // Configure the plot layout based on theme
  const layout = {
    ...chartConfig.layout,
    paper_bgcolor: 'rgba(0,0,0,0)', // Transparent background
    plot_bgcolor: 'rgba(0,0,0,0)',  // Transparent plot area
    scrollZoom: false, // Explicitly disable scroll zoom in layout
    dragmode: false, // Disable drag interactions that might cause scroll issues
    font: {
      color: theme === 'light' ? '#2c3e50' : '#e2e8f0', // Dark blue for light mode, light gray for dark
      family: 'Inter, system-ui, sans-serif',
    },
    // Update axes based on theme
    xaxis: {
      ...chartConfig.layout?.xaxis,
      gridcolor: theme === 'light' ? '#e2e8f0' : '#374151',
      tickcolor: theme === 'light' ? '#2c3e50' : '#6b7280',
      linecolor: theme === 'light' ? '#2c3e50' : '#6b7280',
      zerolinecolor: theme === 'light' ? '#cbd5e1' : '#4b5563',
    },
    yaxis: {
      ...chartConfig.layout?.yaxis,
      gridcolor: theme === 'light' ? '#e2e8f0' : '#374151',
      tickcolor: theme === 'light' ? '#2c3e50' : '#6b7280', 
      linecolor: theme === 'light' ? '#2c3e50' : '#6b7280',
      zerolinecolor: theme === 'light' ? '#cbd5e1' : '#4b5563',
    },
    // Update legend based on theme
    legend: {
      ...chartConfig.layout?.legend,
      font: { color: theme === 'light' ? '#2c3e50' : '#e2e8f0' },
      bgcolor: 'rgba(0,0,0,0)',
    },
  };

  return (
    <Card accent={accent}>
      <div className="p-4">
        {title && (
          <h3 className="text-lg font-medium theme-text-chart-title mb-4">
            {title}
          </h3>
        )}
        <div className="w-full">
          <Plot
            data={chartConfig.data || []}
            layout={layout}
            config={config}
            style={{ width: '100%', height: '400px' }}
            useResizeHandler={true}
            onError={handleError}
            onInitialized={() => {
              // Ensure plot is properly initialized
              console.log('Plot initialized successfully');
            }}
            revision={0} // Prevent unnecessary re-renders
          />
        </div>
      </div>
      </Card>
  );
};